import React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { MAGENTA, cardSx, touchMinTextSx } from "./tokens";

interface RecentCasesCardProps {
  cases: any[];
  loading: boolean;
  onViewAll: () => void;
  onOpenCaseGrid: () => void;
}

const initials = (name: string) =>
  (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const RecentCasesCard: React.FC<RecentCasesCardProps> = ({
  cases,
  loading,
  onViewAll,
  onOpenCaseGrid,
}) => {
  const navigate = useNavigate();

  const linkSx = {
    fontSize: "12px",
    fontWeight: 600,
    color: MAGENTA,
    cursor: "pointer",
    whiteSpace: "nowrap",
    "&:hover": { textDecoration: "underline" },
  };

  return (
    <Box sx={cardSx}>
      <Box
        sx={{
          padding: "16px 18px 14px",
          borderBottom: "1px solid #efeff1",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box
            sx={{
              width: 6,
              height: 20,
              borderRadius: "3px",
              backgroundColor: "#fdb814",
            }}
          />
          <Typography
            sx={{ fontSize: "15px", fontWeight: 800, color: "#26262b" }}
          >
            Recent cases
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* Keeps the IDEACoreCRM case-grid reachable — the redesign drops the
              static crm.png tile that used to be its only entry point. */}
          <Typography sx={linkSx} onClick={onOpenCaseGrid}>
            Case grid
          </Typography>
          <Typography sx={linkSx} onClick={onViewAll}>
            View all
          </Typography>
        </Box>
      </Box>

      <Box sx={{ maxHeight: "360px", overflowY: "auto" }}>
        {cases.length === 0 ? (
          <Typography
            sx={{ padding: "28px 20px", textAlign: "center", fontSize: "14px", color: "#7a7a80" }}
          >
            {loading ? "Loading…" : "No recent cases."}
          </Typography>
        ) : (
          cases.map((c) => {
            const firm = c.firmRecord || {};
            const name = firm["Company Name"] || "Unknown";
            return (
              <Box
                key={String(c._id)}
                onClick={() => navigate(`/case?id=${c._id}`)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "11px 18px",
                  borderBottom: "1px solid #f4f4f6",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#faf8fa" },
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    flexShrink: 0,
                    borderRadius: "9px",
                    backgroundColor: "#f3e6ee",
                    color: MAGENTA,
                    fontSize: "13px",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {initials(name)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{ fontSize: "13.5px", fontWeight: 700, color: MAGENTA }}
                  >
                    {name}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: "12px", color: "#8a8a90" }}>
                    {firm["Contact"] || "—"}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: "11.5px",
                    color: "#9a9aa0",
                    whiteSpace: "nowrap",
                    ...touchMinTextSx,
                  }}
                >
                  {c.lastActivityDate
                    ? moment(c.lastActivityDate).fromNow()
                    : ""}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default React.memo(RecentCasesCard);
